import React from "react";
import { Form, Input, Button, Row, Col, Select, Collapse, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import ModalSpecial from "../../../../components/Layout/Modals/ModalSpecial";
import { prettyDateFormat } from "../../../../helpers/Utils";
import Treeselect from "../../../../components/common/Treeselect";

const ZONE_TYPE_OPTIONS = [
  { value: "category", label: "Category" },
  { value: "product", label: "Product" },
  { value: "page", label: "Page" },
  { value: "brand", label: "Brand" },
  { value: "home", label: "Titulinis" },
];

const DEFAULT_ZONE_TYPE = "category";

const ZONE_POSITION_OPTIONS = [
  { value: "header", label: "Header" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "footer", label: "Footer" },
  { value: "inline", label: "Inline" },
  { value: "description", label: "Description" },
];
import {
  categoryIdsFromTree,
  convertToCodeTreeData,
  convertToTreeData,
  withSelectableParents,
} from "../../../../helpers/ConvertToTreeData";
import RangePickerComp from "../../../../components/common/RangePickerComp";
import BlockBanners from "../components/BlockBanners";
import BlockFields from "../components/BlockFields";
import BlockSkus from "../components/BlockSkus";
import { BLOCK_TYPE_OPTIONS, blockTypeLabel } from "../components/blockTypes";
import { showApiError } from "../components/apiErrors";
import {
  COLUMN_TYPES,
  DEFAULT_BACKGROUND,
  DEFAULT_ITEMS_LIMIT,
  DEFAULT_TEXT_ALIGN,
  DEFAULT_TEXT_COLOR,
  HOVER_ANIMATION_TYPES,
  ITEM_TYPES,
  LOCAL_NAME_TYPES,
  SKU_TYPES,
  TEXT_ALIGN_TYPES,
  VALIDITY_TYPES,
  appendBlockItems,
  appendBlockSkus,
  hydrateBlockBanners,
  hydrateBlockSkus,
  makeBlockBanner,
  normalizeDateForApi,
  paddingValue,
  startsWithBanner,
  toMomentRange,
} from "../components/blockItems";

const { Panel } = Collapse;

const BLOCK_FIELD_SUFFIXES = {
  title: "block_title",
  systemName: "system_name",
  background: "block_bg",
  columns: "columns",
  category: "category",
  localName: "local_name",
  validity: "validity",
  textColor: "text_color",
  textAlign: "text_align",
  paddingTop: "padding_top",
  paddingBottom: "padding_bottom",
  paddingLeft: "padding_left",
  paddingRight: "padding_right",
  itemsLimit: "items_limit",
};

const blockFieldName = (block, field) =>
  `${block.key}_${BLOCK_FIELD_SUFFIXES[field]}`;

const bannerFieldPrefix = (block) => (banner) => `${block.key}_${banner.key}`;

let linkedBlockCounter = 0;
const makeLinkedBlock = (bannerBlock) => {
  linkedBlockCounter += 1;

  return {
    key: `linked_block_${linkedBlockCounter}`,
    type: bannerBlock.block_type,
    linked: true,
    linkId: undefined,
    bannerBlockId: bannerBlock.id,
    title: bannerBlock.title,
    typeLabel: bannerBlock.type_label,
    itemsCount: bannerBlock.items_count,
    validFrom: bannerBlock.valid_from,
    validTo: bannerBlock.valid_to,
    banners: [],
  };
};

let blockCounter = 0;
const makeBlock = (type) => {
  blockCounter += 1;

  return {
    key: `new_block_${blockCounter}`,
    type,
    linkId: undefined,
    bannerBlockId: undefined,
    backgroundColor: DEFAULT_BACKGROUND,
    textColor: DEFAULT_TEXT_COLOR,
    showLocalName: true,
    hoverAnimation: false,
    loadedItems: [],
    skus: [],
    itemsLimit: DEFAULT_ITEMS_LIMIT,
    banners: startsWithBanner(type) ? [makeBlockBanner()] : [],
  };
};

const buildInfoZoneNestedFormData = ({
  blocks,
  values,
  existingShops = [],
  removedLinkIds = [],
}) => {
  const formData = new FormData();

  const appendValue = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    formData.append(key, value);
  };

  appendValue("banner[title]", values.title);
  appendValue("banner[banner_place]", "info_zone");
  appendValue("banner[popup_in]", values.zone_type || "category");
  formData.append("banner[position]", values.position || "");

  const [zoneValidFrom, zoneValidTo] = Array.isArray(values.validity)
    ? values.validity
    : [null, null];
  formData.append("banner[valid_from]", normalizeDateForApi(zoneValidFrom) || "");
  formData.append("banner[valid_to]", normalizeDateForApi(zoneValidTo) || "");

  const selectedCategoryIds = values.categories || [];
  if (selectedCategoryIds.length) {
    selectedCategoryIds.forEach((categoryId) => {
      formData.append("banner[categories][]", categoryId);
    });
  } else {
    formData.append("banner[categories][]", "");
  }

  const selectedShopIds = values.shop_id ? [Number(values.shop_id)] : [];
  const keptShops = selectedShopIds.map((orderShopId) => ({
    id: existingShops.find(
      (shop) => Number(shop.order_shop_id) === orderShopId
    )?.id,
    order_shop_id: orderShopId,
  }));
  const removedShops = existingShops
    .filter((shop) => !selectedShopIds.includes(Number(shop.order_shop_id)))
    .map((shop) => ({ id: shop.id, destroy: true }));

  [...keptShops, ...removedShops].forEach((shop, index) => {
    const shopPrefix = `banner[banner_shops_attributes][${index}]`;
    appendValue(`${shopPrefix}[id]`, shop.id);

    if (shop.destroy) {
      formData.append(`${shopPrefix}[_destroy]`, "true");
      return;
    }

    appendValue(`${shopPrefix}[order_shop_id]`, shop.order_shop_id);
    formData.append(`${shopPrefix}[active]`, "true");
  });

  blocks.forEach((block, blockIndex) => {
    const linkPrefix = `banner[banner_block_links_attributes][${blockIndex}]`;
    const blockPrefix = `${linkPrefix}[banner_block_attributes]`;

    appendValue(`${linkPrefix}[id]`, block.linkId);
    formData.append(`${linkPrefix}[position]`, blockIndex);

    if (block.linked) {
      formData.append(`${linkPrefix}[banner_block_id]`, block.bannerBlockId);
      return;
    }

    appendValue(`${blockPrefix}[id]`, block.bannerBlockId);
    formData.append(`${blockPrefix}[block_type]`, block.type);
    formData.append(
      `${blockPrefix}[title]`,
      values[blockFieldName(block, "title")] || ""
    );
    formData.append(
      `${blockPrefix}[system_name]`,
      values[blockFieldName(block, "systemName")] || ""
    );
    formData.append(
      `${blockPrefix}[background_color]`,
      block.backgroundColor || ""
    );
    formData.append(`${blockPrefix}[text_color]`, block.textColor || "");
    formData.append(
      `${blockPrefix}[padding_top]`,
      paddingValue(values[blockFieldName(block, "paddingTop")]) ?? ""
    );
    formData.append(
      `${blockPrefix}[padding_bottom]`,
      paddingValue(values[blockFieldName(block, "paddingBottom")]) ?? ""
    );
    formData.append(
      `${blockPrefix}[padding_left]`,
      paddingValue(values[blockFieldName(block, "paddingLeft")]) ?? ""
    );
    formData.append(
      `${blockPrefix}[padding_right]`,
      paddingValue(values[blockFieldName(block, "paddingRight")]) ?? ""
    );
    formData.append(
      `${blockPrefix}[columns_count]`,
      COLUMN_TYPES.includes(block.type)
        ? values[blockFieldName(block, "columns")] || ""
        : ""
    );
    formData.append(
      `${blockPrefix}[category_code]`,
      block.type === "best_in_category"
        ? values[blockFieldName(block, "category")] || ""
        : ""
    );

    if (SKU_TYPES.includes(block.type)) {
      formData.append(
        `${blockPrefix}[settings][items_limit]`,
        values[blockFieldName(block, "itemsLimit")] || DEFAULT_ITEMS_LIMIT
      );
    }

    const blockValidity = values[blockFieldName(block, "validity")];
    const [validFrom, validTo] =
      VALIDITY_TYPES.includes(block.type) && Array.isArray(blockValidity)
        ? blockValidity
        : [null, null];
    formData.append(
      `${blockPrefix}[valid_from]`,
      normalizeDateForApi(validFrom)
    );
    formData.append(`${blockPrefix}[valid_to]`, normalizeDateForApi(validTo));

    if (TEXT_ALIGN_TYPES.includes(block.type)) {
      formData.append(
        `${blockPrefix}[settings][text_align]`,
        values[blockFieldName(block, "textAlign")] || DEFAULT_TEXT_ALIGN
      );
    }

    if (HOVER_ANIMATION_TYPES.includes(block.type)) {
      formData.append(
        `${blockPrefix}[settings][hover_animation]`,
        block.hoverAnimation ? "true" : "false"
      );
    }

    if (LOCAL_NAME_TYPES.includes(block.type)) {
      formData.append(
        `${blockPrefix}[local_name]`,
        values[blockFieldName(block, "localName")] || ""
      );
      formData.append(
        `${blockPrefix}[settings][show_local_name]`,
        block.showLocalName ? "true" : "false"
      );
    }

    appendBlockItems(formData, {
      blockType: block.type,
      banners: block.banners,
      values,
      loadedItems: block.loadedItems,
      itemPrefix: (index) =>
        `${blockPrefix}[banner_block_items_attributes][${index}]`,
      fieldPrefixFor: bannerFieldPrefix(block),
    });

    appendBlockSkus(formData, {
      blockType: block.type,
      skus: block.skus,
      loadedItems: block.loadedItems,
      itemPrefix: (index) =>
        `${blockPrefix}[banner_block_items_attributes][${index}]`,
    });
  });

  const keptLinkIds = blocks.map((block) => block.linkId).filter(Boolean);
  removedLinkIds
    .filter((linkId) => !keptLinkIds.includes(linkId))
    .forEach((linkId, index) => {
      const linkPrefix = `banner[banner_block_links_attributes][${
        blocks.length + index
      }]`;
      formData.append(`${linkPrefix}[id]`, linkId);
      formData.append(`${linkPrefix}[_destroy]`, "true");
    });

  return formData;
};

const buildEditState = (banner) => {
  const links = Array.isArray(banner?.banner_block_links)
    ? [...banner.banner_block_links].sort(
        (a, b) =>
          (a.position || 0) - (b.position || 0) || (a.id || 0) - (b.id || 0)
      )
    : [];

  const formValues = { title: banner?.title || "" };
  const blocks = links.map((link, index) => {
    const rawBlock = link.banner_block || {};
    const block = makeBlock(rawBlock.block_type || "grid_banner");

    block.key = `block_${rawBlock.id || link.id || index}`;
    block.linkId = link.id;
    block.bannerBlockId = rawBlock.id;
    block.backgroundColor = rawBlock.background_color || DEFAULT_BACKGROUND;
    block.textColor = rawBlock.text_color || DEFAULT_TEXT_COLOR;
    block.showLocalName = rawBlock.settings?.show_local_name !== false;
    block.hoverAnimation = rawBlock.settings?.hover_animation === true;
    block.loadedItems = rawBlock.banner_block_items || [];
    block.skus = hydrateBlockSkus(block.loadedItems);
    block.itemsLimit = rawBlock.settings?.items_limit || DEFAULT_ITEMS_LIMIT;

    const { banners, values } = hydrateBlockBanners(
      block.type,
      block.loadedItems,
      bannerFieldPrefix(block)
    );
    block.banners = banners;

    Object.assign(formValues, values);
    formValues[blockFieldName(block, "title")] = rawBlock.title || "";
    formValues[blockFieldName(block, "systemName")] = rawBlock.system_name || "";
    formValues[blockFieldName(block, "background")] = block.backgroundColor;
    formValues[blockFieldName(block, "textColor")] = block.textColor;
    formValues[blockFieldName(block, "textAlign")] =
      rawBlock.settings?.text_align || DEFAULT_TEXT_ALIGN;
    formValues[blockFieldName(block, "paddingTop")] = paddingValue(
      rawBlock.padding_top
    );
    formValues[blockFieldName(block, "paddingBottom")] = paddingValue(
      rawBlock.padding_bottom
    );
    formValues[blockFieldName(block, "paddingLeft")] = paddingValue(
      rawBlock.padding_left
    );
    formValues[blockFieldName(block, "paddingRight")] = paddingValue(
      rawBlock.padding_right
    );
    formValues[blockFieldName(block, "columns")] =
      rawBlock.columns_count || undefined;
    formValues[blockFieldName(block, "itemsLimit")] =
      rawBlock.settings?.items_limit || undefined;
    formValues[blockFieldName(block, "localName")] = rawBlock.local_name || "";
    formValues[blockFieldName(block, "category")] =
      rawBlock.category_code || undefined;
    formValues[blockFieldName(block, "validity")] = toMomentRange(
      rawBlock.valid_from,
      rawBlock.valid_to
    );

    return block;
  });

  return { blocks, formValues };
};

const ModalNewInfoZone = ({ onClose, modalOpt = { type: null } }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isEdit = modalOpt?.type === "edit_info_zone";

  const [selectedBlockType, setSelectedBlockType] =
    React.useState("grid_banner");
  const [selectedExistingBlock, setSelectedExistingBlock] =
    React.useState(undefined);
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [zoneType, setZoneType] = React.useState(DEFAULT_ZONE_TYPE);
  const [blocks, setBlocks] = React.useState([]);
  const [removedLinkIds, setRemovedLinkIds] = React.useState([]);

  const {
    data: existingBanner,
    isLoading: isLoadingExistingBanner,
    isFetching: isFetchingExistingBanner,
  } = useQuery(
    ["info-zone-banner", modalOpt?.record_id],
    async () => {
      const response = await axios.get(`/api/v1/banners/${modalOpt.record_id}`);
      return response.data;
    },
    {
      enabled: isEdit && !!modalOpt?.record_id,
      cacheTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: existingBlocks = [], isLoading: isLoadingExistingBlocks } =
    useQuery("info-zone-existing-blocks", async () => {
      const response = await axios.get("/api/v1/banner_blocks?per_page=200");
      return response.data?.data || [];
    });

  const { data: shopOptions = [], isLoading: isLoadingShops } = useQuery(
    "info-zone-modal-order-shops",
    async () => {
      const response = await axios.get("/api/v1/order_shops");
      return response.data?.shops || [];
    }
  );

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery(
    "info-zone-modal-categories",
    async () => {
      const response = await axios.get(
        "/api/v1/categories/simple_list?page=1&per_page=999999"
      );
      return response.data || [];
    }
  );

  const categoryOptions = React.useMemo(
    () => withSelectableParents(convertToTreeData(categories)),
    [categories]
  );

  const blockCategoryOptions = React.useMemo(
    () => convertToCodeTreeData(categories),
    [categories]
  );

  React.useEffect(() => {
    if (!isEdit || !existingBanner) return;

    const { blocks: hydratedBlocks, formValues } =
      buildEditState(existingBanner);

    setBlocks(hydratedBlocks);
    setRemovedLinkIds([]);
    setSelectedBlockType(hydratedBlocks[0]?.type || "grid_banner");

    const loadedCategoryIds = (existingBanner.categories || []).map(
      (category) => category.id
    );
    setSelectedCategories(loadedCategoryIds);
    setZoneType(existingBanner.popup_in || DEFAULT_ZONE_TYPE);

    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        ...formValues,
        validity: toMomentRange(
          existingBanner.valid_from,
          existingBanner.valid_to
        ),
        shop_id: (existingBanner.banner_shops || [])
          .map((shop) => shop.order_shop_id)
          .filter(Boolean)[0],
        categories: loadedCategoryIds,
        zone_type: existingBanner.popup_in || DEFAULT_ZONE_TYPE,
        position: existingBanner.position || undefined,
      });
    }, 0);
  }, [isEdit, existingBanner, form]);

  const addBlock = () => {
    if (!selectedBlockType) return;
    setBlocks((prev) => [...prev, makeBlock(selectedBlockType)]);
  };

  const attachedBlockIds = blocks
    .map((block) => block.bannerBlockId)
    .filter(Boolean);

  const existingBlockOptions = (existingBlocks || [])
    .filter((block) => !attachedBlockIds.includes(block.id))
    .map((block) => ({
      value: block.id,
      label: `${
        block.title || block.system_name || `Block #${block.id}`
      } - ${block.type_label || blockTypeLabel(block.block_type)}${
        block.items_count ? ` (${block.items_count})` : ""
      }`,
    }));

  const addExistingBlock = () => {
    const bannerBlock = (existingBlocks || []).find(
      (block) => block.id === selectedExistingBlock
    );
    if (!bannerBlock) return;

    setBlocks((prev) => [...prev, makeLinkedBlock(bannerBlock)]);
    setSelectedExistingBlock(undefined);
  };

  const moveBlock = (key, offset) => {
    setBlocks((prev) => {
      const index = prev.findIndex((block) => block.key === key);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= prev.length) return prev;

      const reordered = [...prev];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

      return reordered;
    });
  };

  const removeBlock = (key) => {
    const removed = blocks.find((block) => block.key === key);

    if (removed?.linkId) {
      setRemovedLinkIds((prev) =>
        prev.includes(removed.linkId) ? prev : [...prev, removed.linkId]
      );
    }

    setBlocks((prev) => prev.filter((block) => block.key !== key));
  };

  const updateBlock = (key, patch) => {
    setBlocks((prev) =>
      prev.map((block) => (block.key === key ? { ...block, ...patch } : block))
    );
  };

  const addBanner = (blockKey, overrides = {}) => {
    setBlocks((prev) =>
      prev.map((block) =>
        block.key === blockKey
          ? {
              ...block,
              banners: [...block.banners, makeBlockBanner(overrides)],
            }
          : block
      )
    );
  };

  const removeBanner = (blockKey, bannerKey) => {
    setBlocks((prev) =>
      prev.map((block) =>
        block.key === blockKey
          ? {
              ...block,
              banners: block.banners.filter(
                (banner) => banner.key !== bannerKey
              ),
            }
          : block
      )
    );
  };

  const updateBanner = (blockKey, bannerKey, patch) => {
    setBlocks((prev) =>
      prev.map((block) =>
        block.key === blockKey
          ? {
              ...block,
              banners: block.banners.map((banner) =>
                banner.key === bannerKey ? { ...banner, ...patch } : banner
              ),
            }
          : block
      )
    );
  };

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = buildInfoZoneNestedFormData({
        blocks,
        values,
        existingShops: existingBanner?.banner_shops || [],
        removedLinkIds,
      });

      const requestMethod = isEdit ? "put" : "post";
      const recordId = modalOpt?.record_id;
      const requestUrl = isEdit
        ? `/api/v1/banners/${recordId}`
        : "/api/v1/banners";

      return axios[requestMethod](requestUrl, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then((res) => res.data);
    },
    onSuccess: (savedBanner) => {
      queryClient.setQueryData(
        ["info-zone-banner", savedBanner?.id],
        savedBanner
      );
      queryClient.invalidateQueries(["info-zone-banner", savedBanner?.id]);
      queryClient.invalidateQueries("info-zone-banners");
      queryClient.invalidateQueries("info-zone-blocks");
      message.success(isEdit ? "Info zone updated" : "Info zone created");
      onClose();
    },
    onError: (error) => {
      showApiError(
        error,
        isEdit ? "Failed to update info zone" : "Failed to create info zone"
      );
    },
  });

  const onFinishFailed = (errorInfo) => {
    const firstError = errorInfo?.errorFields?.[0]?.errors?.[0];
    message.error(firstError || "Please fill required fields before saving.");
  };

  return (
    <ModalSpecial
      style={{ width: 570 }}
      text={
        <span className="font-bold text-xl text-black">
          {isEdit ? "Edit" : "Add New"} Info Zone
        </span>
      }
      onClose={onClose}
    >
      {isEdit && (isLoadingExistingBanner || isFetchingExistingBanner) ? (
        <div className="text-center text-muted py-4">
          Loading info zone data...
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          name="info_zone_form"
          onFinish={(values) => mutation.mutate(values)}
          onFinishFailed={onFinishFailed}
          onValuesChange={(changed) => {
            Object.keys(changed).forEach((field) => {
              if (!field.endsWith("_items_limit")) return;

              const blockKey = field.replace(/_items_limit$/, "");
              const next = Number(changed[field]);
              updateBlock(blockKey, {
                itemsLimit:
                  Number.isInteger(next) && next > 0
                    ? next
                    : DEFAULT_ITEMS_LIMIT,
              });
            });
          }}
          scrollToFirstError
          autoComplete="off"
          initialValues={{
            zone_type: DEFAULT_ZONE_TYPE,
            categories: [],
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: "Can't be empty" }]}
              >
                <Input placeholder="Type in" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Validity period" name="validity">
                <RangePickerComp
                  style={{ width: "100%" }}
                  showTime={{ format: "HH:mm" }}
                  placeholder={["Start time", "End time"]}
                  disabledDate={() => false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Type"
                name="zone_type"
                rules={[{ required: true, message: "Can't be empty" }]}
              >
                <Select
                  placeholder="Select"
                  options={ZONE_TYPE_OPTIONS}
                  onChange={(value) => {
                    setZoneType(value);
                    if (value !== "category") {
                      setSelectedCategories([]);
                      form.setFieldsValue({ categories: [] });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Position"
                name="position"
                rules={[{ required: true, message: "Can't be empty" }]}
              >
                <Select
                  placeholder="Select an option"
                  options={ZONE_POSITION_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Shop" name="shop_id">
                <Select
                  allowClear
                  showSearch
                  placeholder="Select shop"
                  optionFilterProp="label"
                  options={shopOptions}
                  loading={isLoadingShops}
                />
              </Form.Item>
            </Col>
            {zoneType === "category" && (
              <Col span={12}>
                <Form.Item label="Categories" name="categories">
                  <Treeselect
                    options={categoryOptions}
                    value={selectedCategories}
                    onChange={(values) => {
                      const categoryIds = categoryIdsFromTree(values);
                      setSelectedCategories(categoryIds);
                      form.setFieldsValue({ categories: categoryIds });
                    }}
                    placeholder={
                      isLoadingCategories
                        ? "Loading categories..."
                        : "Select categories..."
                    }
                    multiSelect
                    searchable
                    dropdownWidth="500px"
                    showStrategy="SHOW_ALL"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item label="Existing block" style={{ marginBottom: 8 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Select
                style={{ flex: 1 }}
                placeholder="Select a block that is already created"
                allowClear
                showSearch
                optionFilterProp="label"
                value={selectedExistingBlock}
                onChange={setSelectedExistingBlock}
                options={existingBlockOptions}
                loading={isLoadingExistingBlocks}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                disabled={!selectedExistingBlock}
                onClick={addExistingBlock}
              />
            </div>
          </Form.Item>

          <Form.Item label="Block Category" required style={{ marginBottom: 8 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Select
                style={{ flex: 1 }}
                placeholder="Select"
                value={selectedBlockType}
                onChange={setSelectedBlockType}
                options={BLOCK_TYPE_OPTIONS}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                onClick={addBlock}
              />
            </div>
          </Form.Item>

          {!blocks.length && (
            <div className="text-muted mt-3" style={{ fontSize: 13 }}>
              No blocks attached yet.
            </div>
          )}

          {blocks.length > 0 && (
            <Collapse
              defaultActiveKey={blocks.map((block) => block.key)}
              expandIconPosition="end"
              className="mt-3"
            >
              {blocks.map((block, blockIndex) => (
                <Panel
                  key={block.key}
                  forceRender
                  header={
                    <span className="text-primary font-weight-bold">
                      {block.linked
                        ? `${block.title || `Block #${block.bannerBlockId}`} - ${
                            block.typeLabel || blockTypeLabel(block.type)
                          }`
                        : `Add ${blockTypeLabel(block.type)}`}
                    </span>
                  }
                  extra={
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: 10 }}
                    >
                      <ArrowUpOutlined
                        style={{
                          color: blockIndex === 0 ? "#D9D9D9" : "#454545",
                          cursor: blockIndex === 0 ? "not-allowed" : "pointer",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          moveBlock(block.key, -1);
                        }}
                      />
                      <ArrowDownOutlined
                        style={{
                          color:
                            blockIndex === blocks.length - 1
                              ? "#D9D9D9"
                              : "#454545",
                          cursor:
                            blockIndex === blocks.length - 1
                              ? "not-allowed"
                              : "pointer",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          moveBlock(block.key, 1);
                        }}
                      />
                      <DeleteOutlined
                        style={{ color: "#C94747" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeBlock(block.key);
                        }}
                      />
                    </div>
                  }
                >
                  {block.linked ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      <div>
                        Banners in block: {block.itemsCount ?? 0}
                      </div>
                      <div>
                        Validity:{" "}
                        {block.validFrom
                          ? prettyDateFormat(block.validFrom)
                          : "No start date"}{" "}
                        -{" "}
                        {block.validTo
                          ? prettyDateFormat(block.validTo)
                          : "No end date"}
                      </div>
                      <div className="mt-2" style={{ fontSize: 12 }}>
                        This block is only attached to the info zone. Its content
                        is edited in the Blocks tab.
                      </div>
                    </div>
                  ) : (
                    <>
                  <BlockFields
                    blockType={block.type}
                    nameFor={(field) => blockFieldName(block, field)}
                    backgroundColor={block.backgroundColor}
                    onBackgroundColorChange={(value) =>
                      updateBlock(block.key, { backgroundColor: value })
                    }
                    textColor={block.textColor}
                    onTextColorChange={(value) =>
                      updateBlock(block.key, { textColor: value })
                    }
                    showLocalName={block.showLocalName}
                    onShowLocalNameChange={() =>
                      updateBlock(block.key, {
                        showLocalName: !block.showLocalName,
                      })
                    }
                    hoverAnimation={block.hoverAnimation}
                    onHoverAnimationChange={() =>
                      updateBlock(block.key, {
                        hoverAnimation: !block.hoverAnimation,
                      })
                    }
                    categoryOptions={blockCategoryOptions}
                    skuCount={
                      (block.skus || []).filter((entry) => entry.sku).length
                    }
                    itemsLimit={block.itemsLimit || DEFAULT_ITEMS_LIMIT}
                  />

                  {SKU_TYPES.includes(block.type) && (
                    <BlockSkus
                      skus={block.skus || []}
                      itemsLimit={block.itemsLimit || DEFAULT_ITEMS_LIMIT}
                      onChange={(nextSkus) =>
                        updateBlock(block.key, { skus: nextSkus })
                      }
                    />
                  )}

                  {ITEM_TYPES.includes(block.type) && (
                    <BlockBanners
                      blockType={block.type}
                      typeLabel={blockTypeLabel(block.type)}
                      banners={block.banners}
                      fieldPrefixFor={bannerFieldPrefix(block)}
                      onAdd={(overrides) => addBanner(block.key, overrides)}
                      onRemove={(bannerKey) => removeBanner(block.key, bannerKey)}
                      onUpdate={(bannerKey, patch) =>
                        updateBanner(block.key, bannerKey, patch)
                      }
                      form={form}
                    />
                  )}
                    </>
                  )}
                </Panel>
              ))}
            </Collapse>
          )}

          <Form.Item className="mt-3">
            <Button
              className="w-100"
              type="primary"
              htmlType="submit"
              loading={
                mutation.isLoading ||
                isLoadingExistingBanner ||
                isFetchingExistingBanner
              }
            >
              {isEdit ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </ModalSpecial>
  );
};

export default ModalNewInfoZone;
